import React, { memo } from "react";
import { IconLogout } from "@tabler/icons-react";
import { formatDate, formatRelativeTime, Device, DeviceGroup } from "../deviceManagement.helper";

interface DeviceRowProps {
  group: DeviceGroup;
  device: Device;
  isSelected: boolean;
  onCheckboxChange: (deviceId: string, isChecked: boolean) => void;
  onDeviceClick: (device: Device) => void;
  onLogoutDevice: (deviceId: string) => void;
  columnVisibility?: Record<string, boolean>;
}

/**
 * A single row representing a device in the flat device management table.
 */
const DeviceRow: React.FC<DeviceRowProps> = ({
  group,
  device,
  isSelected,
  onCheckboxChange,
  onDeviceClick,
  onLogoutDevice,
  columnVisibility = {},
}) => {
  const isBlocked = device.status === "Bị chặn";
  const isActive = device.status === "Hoạt động";

  const isVisible = (key: string) => columnVisibility[key] !== false;

  return (
    <tr>
      <td className="text-center" style={{ width: "40px" }}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={isSelected}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onCheckboxChange(device.id, e.target.checked)
          }
        />
      </td>

      {isVisible("deviceName") && (
        <td style={{ maxWidth: "200px" }}>
          <span
            className={`cursor-pointer fw-semibold text-reset text-truncate d-inline-block w-100 align-middle ${
              isBlocked ? "text-danger text-decoration-line-through" : ""
            }`}
            onClick={() => onDeviceClick(device)}
            title={device.deviceName || "—"}
          >
            {device.deviceName || "—"}
          </span>
        </td>
      )}

      {isVisible("accountName") && (
        <td style={{ maxWidth: "160px" }}>
          <span
            className={`fw-medium text-truncate d-inline-block w-100 align-middle ${
              isBlocked ? "text-muted" : "text-body"
            }`}
            title={group.accountName || "—"}
          >
            {group.accountName || "—"}
          </span>
        </td>
      )}

      {isVisible("phone") && (
        <td style={{ maxWidth: "180px" }}>
          <span
            className="text-secondary text-truncate d-inline-block w-100 align-middle"
            title={group.phone || "—"}
          >
            {group.phone || "—"}
          </span>
        </td>
      )}

      {isVisible("role") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{device.role || "—"}</span>
        </td>
      )}

      {isVisible("type") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{device.type || "—"}</span>
        </td>
      )}

      {isVisible("os") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{device.os || "—"}</span>
        </td>
      )}

      {isVisible("status") && (
        <td>
          <span
            className={`badge ${
              isBlocked
                ? "bg-danger-subtle text-danger"
                : isActive
                ? "bg-success-subtle text-success"
                : "bg-secondary-subtle text-secondary"
            }`}
          >
            {device.status}
          </span>
        </td>
      )}

      {isVisible("firstLoginDate") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{formatDate(device.firstLoginDate)}</span>
        </td>
      )}

      {isVisible("version") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{device.version || "—"}</span>
        </td>
      )}

      {isVisible("lastActivity") && (
        <td>
          <span className={isBlocked ? "text-muted" : ""}>{formatRelativeTime(device.lastActivity)}</span>
        </td>
      )}

      {isVisible("actions") && (
        <td className="text-center">
          <button
            type="button"
            className="btn btn-icon btn-ghost-danger btn-sm"
            title="Đăng xuất"
            onClick={() => onLogoutDevice(device.id)}
          >
            <IconLogout size={16} />
          </button>
        </td>
      )}
    </tr>
  );
};

export default memo(DeviceRow);
